import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { seoApi } from "@/lib/seoApi";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, CheckCircle2, PencilLine, ChevronDown, ChevronUp, Loader } from "lucide-react";

const IN_FLIGHT = ["rewriting", "republishing", "publishing", "queued"];

export default function ReviewQueue() {
  const [data, setData] = useState(null);
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    seoApi.get("/seo/workflows/review-queue")
      .then((r) => setData(r.data)).catch(() => setData({ count: 0, items: [] }));
  }, []);

  useEffect(() => {
    load();
    seoApi.get("/acf/security/whoami")
      .then((r) => setRole(r.data?.role || "")).catch(() => setRole(""));
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  async function act(runId, path, label, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(runId);
    try {
      const r = await seoApi.post(`/seo/workflows/runs/${runId}/${path}`);
      toast.success(`${label}: ${r.data?.message || "ok"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  }

  const isAdmin = role === "super-admin";
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="review-queue-page">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ShieldAlert size={22} className="text-orange-600" /> Review Queue
        </h1>
        <p className="text-neutral-500 text-sm mt-1">
          Articles blocked by the Uniqueness Gate. Fix, approve or edit before they go live.
        </p>
      </div>

      {data === null && <div className="text-neutral-500" data-testid="review-loading">Loading…</div>}
      {data !== null && items.length === 0 && (
        <Card data-testid="review-empty">
          <CardContent className="p-8 text-center text-neutral-500">
            <CheckCircle2 size={28} className="mx-auto mb-2 text-emerald-600" />
            Queue is empty — every article passed the uniqueness gate.
          </CardContent>
        </Card>
      )}

      {items.map((it) => (
        <QueueItem key={it.run_id} item={it} isAdmin={isAdmin}
                    busy={busy === it.run_id} onAct={act} />
      ))}
    </div>
  );
}

function QueueItem({ item, isAdmin, busy, onAct }) {
  const [open, setOpen] = useState(false);
  const pct = item.prose_uniqueness_pct;
  return (
    <Card data-testid={`review-item-${item.run_id.slice(0, 8)}`}>
      <CardHeader className="flex-row items-start justify-between gap-3 pb-2">
        <div className="min-w-0">
          <CardTitle className="text-base truncate">
            {item.brief_title || item.slug || item.run_id.slice(0, 8)}
          </CardTitle>
          <div className="text-xs text-neutral-500 mt-1 font-mono truncate">{item.slug}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge className="bg-orange-100 text-orange-800">
            prose {pct != null ? `${pct}%` : "—"} / need {item.threshold}%
          </Badge>
          {item.is_published && <Badge variant="outline">live</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm text-neutral-700" data-testid="review-reason">
          <span className="text-neutral-500">Reason:</span>{" "}
          {item.publish_error || item.reason || "below threshold"}
        </div>
        {item.breakdown && (
          <div className="text-xs text-neutral-500">
            Overlap: {item.breakdown.matched_authorial} authorial words /{" "}
            {item.breakdown.matched_immutable} immutable
            ({item.breakdown.immutable_share_pct}% of overlap is fixed data — HS codes, Incoterms, regulations)
          </div>
        )}
        {item.iterations?.length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs">
            {item.iterations.map((i) => (
              <span key={i.iteration} className="px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
                #{i.iteration}: {i.prose_uniqueness_pct}%
                {i.blocks_rewritten > 0 && ` → rewrote ${i.blocks_rewritten}`}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button size="sm" variant="default" disabled={busy}
                  data-testid={`btn-rewrite-again-${item.run_id.slice(0, 8)}`}
                  onClick={() => onAct(item.run_id, "uniqueness-rewrite", "Rewrite again",
                    "Run another rewrite cycle? This spends one more Text.ru check + LLM call.")}>
            <RefreshCw size={14} className="mr-1" /> Rewrite again
          </Button>
          {isAdmin && (
            <Button size="sm" variant="destructive" disabled={busy}
                    data-testid={`btn-approve-${item.run_id.slice(0, 8)}`}
                    onClick={() => onAct(item.run_id, "approve-uniqueness", "Approve",
                      "Approve DESPITE low uniqueness? The article will be published/updated as-is.")}>
            <CheckCircle2 size={14} className="mr-1" /> Approve
            </Button>
          )}
          <Link to={`/seo-studio/runs/${item.run_id}`}>
            <Button size="sm" variant="outline" data-testid={`btn-edit-${item.run_id.slice(0, 8)}`}>
              <PencilLine size={14} className="mr-1" /> Edit manually
            </Button>
          </Link>
          <Button size="sm" variant="ghost" onClick={() => setOpen(!open)}
                  data-testid={`btn-diff-${item.run_id.slice(0, 8)}`}>
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Diff before / after rewrite
          </Button>
          {busy && <Loader size={14} className="animate-spin text-neutral-400" />}
        </div>

        {open && <RewriteDiff runId={item.run_id} />}
      </CardContent>
    </Card>
  );
}

function RewriteDiff({ runId }) {
  const [state, setState] = useState({ loading: true, pairs: null, error: null });

  useEffect(() => {
    seoApi.get(`/seo/workflows/runs/${runId}/content`)
      .then((r) => {
        const versions = r.data?.versions || [];
        const lastRewriteIdx = versions.map((v) => v.source).lastIndexOf("uniqueness_rewrite");
        if (lastRewriteIdx < 1) {
          setState({ loading: false, pairs: null, error: "No rewrite version recorded for this run." });
          return;
        }
        const after = versions[lastRewriteIdx].content || "";
        // "before" = the version immediately preceding the rewrite chain
        let beforeIdx = lastRewriteIdx - 1;
        while (beforeIdx > 0 && versions[beforeIdx].source === "uniqueness_rewrite") beforeIdx -= 1;
        const before = versions[beforeIdx].content || "";
        const ob = before.split(/\n\s*\n/);
        const nb = after.split(/\n\s*\n/);
        const pairs = [];
        const n = Math.min(ob.length, nb.length);
        for (let i = 0; i < n; i += 1) {
          if (ob[i].trim() !== nb[i].trim()) pairs.push({ i, old: ob[i], neu: nb[i] });
        }
        setState({ loading: false, pairs, error: null });
      })
      .catch((e) => setState({ loading: false, pairs: null, error: e?.response?.data?.detail || e.message }));
  }, [runId]);

  if (state.loading) return <div className="text-xs text-neutral-500">Loading diff…</div>;
  if (state.error) return <div className="text-xs text-neutral-500">{state.error}</div>;
  if (!state.pairs?.length) return <div className="text-xs text-neutral-500">No block-level changes found.</div>;

  return (
    <div className="space-y-3 border-t border-neutral-100 pt-3" data-testid="rewrite-diff">
      <div className="text-xs text-neutral-500">{state.pairs.length} rewritten block(s)</div>
      {state.pairs.slice(0, 20).map((p) => (
        <div key={p.i} className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          <div className="rounded border border-red-200 bg-red-50/50 p-2 whitespace-pre-wrap">
            <div className="text-[10px] uppercase text-red-600 mb-1">Before</div>
            {p.old}
          </div>
          <div className="rounded border border-emerald-200 bg-emerald-50/50 p-2 whitespace-pre-wrap">
            <div className="text-[10px] uppercase text-emerald-700 mb-1">After</div>
            {p.neu}
          </div>
        </div>
      ))}
    </div>
  );
}
